import React from 'react';

export interface TicketHolder {
  name: string;
  documentNumber: string;
  email?: string;
  cellNumber?: string;
  ticketType: 'Meia' | 'Inteira';
  comprovanteNumber?: string;
}

interface TicketItemProps {
  index: number;
  ticketHolder: TicketHolder;
  onChange: (index: number, field: keyof TicketHolder, value: string) => void;
  onAddTicket: () => void;
  onRemoveTicket: (index: number) => void;
  hideTicketType?: boolean;
}

const TicketItem: React.FC<TicketItemProps> = ({
  index,
  ticketHolder,
  onChange,
  onAddTicket,
  onRemoveTicket,
  hideTicketType,
}) => {
  const isPrimary = index === 0;

  const handleNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onChange(index, 'name', event.target.value);
  };

  const handleDocumentNumberChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onChange(index, 'documentNumber', event.target.value);
  };

  const handleEmailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onChange(index, 'email', event.target.value);
  };

  const handleCellNumberChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onChange(index, 'cellNumber', event.target.value);
  };

  const handleTicketTypeChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    onChange(index, 'ticketType', event.target.value as 'Meia' | 'Inteira');
    onAddTicket(); // Re-trigger initialization with updated amount
  };

  return (
    <div className={`ticket-card ${isPrimary ? 'ticket-card-primary' : ''}`}>
      <div className="ticket-card-header">
        <div className="ticket-badge-wrap">
          <span className="ticket-index-tag">
            {isPrimary ? 'Ingresso 01 • Titular' : `Ingresso 0${index + 1} • Acompanhante`}
          </span>
          <span className={`ticket-type-tag ${ticketHolder.ticketType === 'Meia' ? 'tag-meia' : 'tag-inteira'}`}>
            {ticketHolder.ticketType}
          </span>
        </div>
        {!isPrimary && (
          <button
            type="button"
            className="ticket-remove-btn"
            onClick={() => onRemoveTicket(index)}
            title="Remover este ingresso"
            aria-label={`Remover ingresso ${index + 1}`}
          >
            Remover
          </button>
        )}
      </div>

      <div className="ticket-fields-grid">
        <div className="ticket-field-group">
          <label htmlFor={`name-${index}`} className="ticket-label">
            Nome Completo <span className="req">*</span>
          </label>
          <input
            type="text"
            id={`name-${index}`}
            className="ticket-input"
            placeholder="Nome do titular do ingresso"
            value={ticketHolder.name}
            onChange={handleNameChange}
            required
          />
        </div>

        <div className="ticket-field-group">
          <label htmlFor={`documentNumber-${index}`} className="ticket-label">
            Documento (CPF ou RG) <span className="req">*</span>
          </label>
          <input
            type="text"
            id={`documentNumber-${index}`}
            className="ticket-input"
            placeholder="000.000.000-00"
            value={ticketHolder.documentNumber}
            onChange={handleDocumentNumberChange}
            required
          />
        </div>

        {isPrimary && (
          <>
            <div className="ticket-field-group">
              <label htmlFor={`email-${index}`} className="ticket-label">
                E-mail (receberá os ingressos) <span className="req">*</span>
              </label>
              <input
                type="email"
                id={`email-${index}`}
                className="ticket-input"
                placeholder="seu.email@exemplo.com"
                value={ticketHolder.email || ''}
                onChange={handleEmailChange}
                required
              />
            </div>

            <div className="ticket-field-group">
              <label htmlFor={`cellNumber-${index}`} className="ticket-label">
                Telefone / WhatsApp <span className="req">*</span>
              </label>
              <input
                type="tel"
                id={`cellNumber-${index}`}
                className="ticket-input"
                placeholder="(11) 99999-9999"
                value={ticketHolder.cellNumber || ''}
                onChange={handleCellNumberChange}
                required
              />
            </div>
          </>
        )}

        {!hideTicketType && (
          <div className="ticket-field-group ticket-field-full">
            <label htmlFor={`ticketType-${index}`} className="ticket-label">
              Modalidade de Entrada <span className="req">*</span>
            </label>
            <div className="ticket-select-wrap">
              <select
                id={`ticketType-${index}`}
                className="ticket-select"
                value={ticketHolder.ticketType}
                onChange={handleTicketTypeChange}
              >
                <option value="Inteira">Ingresso Inteira</option>
                <option value="Meia">Meia-entrada</option>
              </select>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TicketItem;